import {
  Box,
  Button,
  Divider,
  Heading,
  Link,
  StackView,
  Text
} from "@planningcenter/tapestry-react"
import { array, number, oneOfType, shape, string, func } from "prop-types"
import { useMemo, useState } from "react"
import pendoTrackEvent from "utils/pendo_track_event"

const CHECK_INS_PURPLE = "var(--t-fill-color-product-checkins-base)"

IntegrationSetup.propTypes = {
  checkInsBaseUrl: string,
  event: shape({
    id: oneOfType([string, number]),
    name: string
  }),
  filterOptions: shape({
    assignment: array
  })
}
export function IntegrationSetup({ checkInsBaseUrl, event, filterOptions }) {
  const [showAssignments, setShowAssignments] = useState(false)

  const links = useMemo(() => {
    const assignmentTypes = filterOptions.assignment
    return {
      event: eventLink(checkInsBaseUrl, event),
      all: allAssignmentsLink(checkInsBaseUrl, event, assignmentTypes),
      singles: assignmentTypes.map(type => singleAssignmentLink(checkInsBaseUrl, event, type))
    }
  }, [checkInsBaseUrl, event, filterOptions])

  return (
    <>
      <div>
        <Heading level={3} weight={600} marginBottom={1}>
          Link attendees
        </Heading>
        <Text marginBottom={1}>
          Link registered guests to your Check-Ins event and manually create locations.{" "}
          <Link href="https://pcocheck-ins.zendesk.com/hc/en-us/articles/25103685567515" external>
            Learn more
          </Link>
        </Text>
        <CheckInsFormButton {...links.event} />
        <br />
        <Heading level={3} weight={600} marginBottom={1}>
          Link attendees and assignments
        </Heading>
        <Text marginBottom={1}>
          Link registered guests to your Check-Ins event and create Check-Ins locations based on
          your assignment types.{" "}
          <Link href="https://pcocheck-ins.zendesk.com/hc/en-us/articles/25103685567515" external>
            Learn more
          </Link>
        </Text>
        <CheckInsFormButton {...links.all} />
        <Box
          className="action-list__item font-size-4"
          display="inline-block"
          marginVertical={0.5}
          marginLeft={2.5}
          padding={1}
        >
          Newly created assignments will be automatically synced.
        </Box>
        {links.singles.length > 0 && !showAssignments ? (
          <>
            <Divider marginTop={2} marginBottom={2} />
            <Button theme="link" size={4} onClick={() => setShowAssignments(true)}>
              Show individual assignments
            </Button>
          </>
        ) : (
          <StackView alignment="start">
            {links.singles.map(link => (
              <CheckInsFormButton key={link.key} {...link} />
            ))}
          </StackView>
        )}
      </div>
    </>
  )
}

function eventLink(checkInsBaseUrl, { id, name }) {
  return {
    url: `${checkInsBaseUrl}/registrations/?event_id=${id}&event_name=${name}&integration_level=Event`,
    name,
    sendToPendo: () => {
      pendoTrackEvent("create_checkistrations_event", {
        type: "Link Attendees",
        event_id: id,
        event_name: name
      })
    }
  }
}

function allAssignmentsLink(checkInsBaseUrl, { id, name }, assignmentTypes) {
  const assignmentTypeParams = assignmentTypes
    .map(assignmentType => `assignment_type_ids[]=${encodeURIComponent(assignmentType.id)}`)
    .join("&")
  const fullName = `${name}: All Assignments`
  return {
    url: `${checkInsBaseUrl}/registrations/?event_id=${id}&event_name=${encodeURIComponent(
      fullName
    )}&integration_level=AssignmentType&sync_future_assignment_types=1&${assignmentTypeParams}`,
    name: fullName,
    sendToPendo: () => {
      pendoTrackEvent("create_checkistrations_event", {
        type: "Link All Assignments",
        event_id: id,
        event_name: name
      })
    }
  }
}

function singleAssignmentLink(checkInsBaseUrl, { id, name }, assignmentType) {
  const fullName = `${name}: ${assignmentType.name}`
  return {
    key: assignmentType.id,
    url: `${checkInsBaseUrl}/registrations/?event_id=${id}&event_name=${encodeURIComponent(
      fullName
    )}&assignment_type_id=${
      assignmentType.id
    }&sync_future_assignment_types=0&integration_level=AssignmentType&assignment_type_ids[]=${encodeURIComponent(
      assignmentType.id
    )}`,
    name: fullName,
    sendToPendo: () => {
      pendoTrackEvent("create_checkistrations_event", {
        type: "Link Individual Assignment",
        event_id: id,
        event_name: name,
        assignment_type_id: assignmentType.id,
        assignment_type_name: assignmentType.name
      })
    }
  }
}

CheckInsFormButton.propTypes = {
  url: string,
  name: string,
  sendToPendo: func
}
function CheckInsFormButton({ url, name, sendToPendo }) {
  const handleSubmit = e => {
    e.preventDefault()
    sendToPendo()
    e.target.submit()
  }

  const authenticityToken = document.querySelector("head meta[name='csrf-token']").content
  return (
    <form action={url} method="post" target="_blank" css={{ padding: 0 }} onSubmit={handleSubmit}>
      <input type="hidden" name="authenticity_token" value={authenticityToken} />
      <Button
        type="submit"
        iconLeft={{ name: "logomark.checkIns", color: CHECK_INS_PURPLE }}
        title={name}
        theme="link"
      />
    </form>
  )
}
